import { CheckCircle2, Clock, RefreshCcw, Search } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";

/**
 * Yusi Actions — Phase 1.
 * Pure presentational chip. Maps the existing task `status` enum to a
 * user-facing "action state" without mutating the underlying status value.
 *
 * Backend (lang-translated) values supported:
 *   "Bekliyor" | "Pending"       → detected
 *   "Uygulandı" | "Applied"      → applied
 *   "Yeniden Kontrol Edildi" | "Rechecked" → rechecked
 *   "Tamamlandı" | "Completed"   → completed
 */

const COPY = {
  tr: {
    detected: "Tespit edildi",
    applied: "Uygulandı",
    rechecked: "Yeniden kontrol edildi",
    completed: "Tamamlandı",
  },
  en: {
    detected: "Detected",
    applied: "Applied",
    rechecked: "Rechecked",
    completed: "Completed",
  },
};

const STYLES = {
  detected: { icon: Search, cls: "bg-cyan-500/10 text-cyan-300 border-cyan-500/25" },
  applied: { icon: Clock, cls: "bg-blue-500/10 text-blue-300 border-blue-500/25" },
  rechecked: { icon: RefreshCcw, cls: "bg-purple-500/10 text-purple-300 border-purple-500/25" },
  completed: { icon: CheckCircle2, cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/25" },
};

export function statusToActionState(status) {
  switch (status) {
    case "Tamamlandı":
    case "Completed":
      return "completed";
    case "Yeniden Kontrol Edildi":
    case "Rechecked":
      return "rechecked";
    case "Uygulandı":
    case "Applied":
      return "applied";
    case "Bekliyor":
    case "Pending":
    default:
      return "detected";
  }
}

export default function ActionStateChip({ status }) {
  const { lang } = useLang();
  const state = statusToActionState(status);
  const t = COPY[lang] || COPY.tr;
  const meta = STYLES[state];
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${meta.cls}`}
      data-testid={`action-state-${state}`}
    >
      <Icon size={10} strokeWidth={2} />
      {t[state]}
    </span>
  );
}
